import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { v4 } from 'uuid'
import Select from 'react-select/async'

import { deleteAction, addAction, updateAction } from 'Actions/flow'
import { StepContext } from 'Src/containers/contexts'
import { getAllAnswers } from 'Selectors/answers'

import api from 'Src/api'

const TYPE_NAME = 'answer'

const serializeAnswers = (answers) => {
  const activeAnswers = answers.filter((answer) => answer.is_active)
  return activeAnswers.map((answer) => ({ label: answer.name, value: answer.id }))
}

const searchAnswers = _.debounce(
  (value, cb) => {
    api
      .get('v1/answers', { params: { search: value } })
      .then(({ data }) => {
        const d = serializeAnswers(data.results)
        cb(d)
      })
      .catch((err) => {
        console.error('Error while searching answers', err)
      })
  },
  500,
  { leading: true }
)

const AnswerEdit = (props) => {
  const { mode = 'edit', actionId, activeAnswer = null, cancelFn = _.noop, disableOptions = false } = props

  const dispatch = useDispatch()
  const { stepId } = useContext(StepContext)

  const [selectedAnswer, setSelectedAnswer] = useState(null)
  const answers = useSelector(getAllAnswers)

  const canShowDeleteBtn = mode === 'edit'

  useEffect(() => {
    if (!activeAnswer) {
      return
    }
    setSelectedAnswer({ value: activeAnswer.id, label: activeAnswer.name })
  }, [activeAnswer])

  const defaultOptions = useMemo(() => {
    if (!answers) {
      return []
    }

    const options = serializeAnswers(answers)
    if (!selectedAnswer || answers.find((answer) => answer.id === selectedAnswer.value)) {
      return options
    } else {
      return [selectedAnswer, ...options]
    }
  }, [answers, selectedAnswer])

  const update = useCallback(() => {
    dispatch(
      updateAction({
        stepId,
        actionId,
        setting: {
          type: TYPE_NAME,
          answer_id: selectedAnswer.value
        }
      })
    )
  }, [dispatch, stepId, actionId, selectedAnswer])

  const create = useCallback(() => {
    dispatch(
      addAction({
        stepId,
        actionId: v4(),
        setting: {
          type: TYPE_NAME,
          answer_id: selectedAnswer.value
        }
      })
    )
  }, [dispatch, stepId, selectedAnswer])

  const onSaveClick = useCallback(
    (e) => {
      e && e.preventDefault()

      if (mode === 'create') {
        create()
      } else {
        update()
      }

      cancelFn()
    },
    [cancelFn, mode, create, update]
  )

  const onDeleteClick = useCallback(() => {
    dispatch(
      deleteAction({
        stepId,
        actionId
      })
    )
  }, [dispatch, actionId, stepId])

  const onChange = useCallback(
    (option) => {
      setSelectedAnswer(option)
    },
    [setSelectedAnswer]
  )

  return (
    <form>
      <div className='form-group'>
        <div className='label mb-2'>Select Answer</div>
        <Select
          cacheOptions
          menuPortalTarget={document.body}
          menuPosition='fixed'
          defaultOptions={defaultOptions}
          loadOptions={searchAnswers}
          onChange={onChange}
          value={selectedAnswer}
        />
      </div>
      {!disableOptions && (
        <div className='options'>
          <button className='button-primary small save' onClick={onSaveClick}>
            Save
          </button>
          <button className='btn cancel' onClick={cancelFn}>
            Cancel
          </button>
          {canShowDeleteBtn && (
            <button className='btn delete' onClick={onDeleteClick}>
              Delete
            </button>
          )}
        </div>
      )}
    </form>
  )
}

export default AnswerEdit
